// How a printed cookbook looks. The builder's preview and the PDF (cookbook/pdf.ts) are drawn by the same code from
// these choices, so the book turned on screen is the book that's printed. The named looks are starting points; changing
// any choice makes it the maker's own. Nothing here changes a photo: edge to edge crops only what's printed.

import { COOKBOOK, COOKBOOK_RECIPE, readDesign, type PageEdits } from '../../../shared/printDesign'

export type CookbookLook = {
  // A recipe's picture on the left-hand page facing it, or above the recipe on the same page.
  layout: (typeof COOKBOOK.layout)[number]
  // Pictures run off the edges, or sit framed in white space.
  pictures: (typeof COOKBOOK.pictures)[number]
  // Titles in the book's serif, or in the app's sans.
  type: (typeof COOKBOOK.type)[number]
  paper: (typeof COOKBOOK.paper)[number]
  // The front: a round plate of food under the title, a whole picture, or the title alone.
  cover: (typeof COOKBOOK.cover)[number]
  // How the photos print: as they are, black and white, or sepia.
  photos: (typeof COOKBOOK.photos)[number]
}

export const COOKBOOK_LOOKS = {
  // The cookbook as it printed before the builder.
  classic: {
    name: 'Classic',
    blurb: 'Picture facing the recipe, serif type',
    look: {
      layout: 'facing',
      pictures: 'bleed',
      type: 'traditional',
      paper: 'white',
      cover: 'plate',
      photos: 'color',
    },
  },
  gallery: {
    name: 'Gallery',
    blurb: 'Small framed pictures, lots of air',
    look: {
      layout: 'facing',
      pictures: 'framed',
      type: 'traditional',
      paper: 'natural',
      cover: 'words',
      photos: 'color',
    },
  },
  bleed: {
    name: 'Edge to edge',
    blurb: 'Full-page food pictures',
    look: { layout: 'facing', pictures: 'bleed', type: 'modern', paper: 'white', cover: 'photo', photos: 'color' },
  },
  kitchen: {
    name: 'Kitchen',
    blurb: 'Picture and recipe on one page',
    look: { layout: 'one', pictures: 'bleed', type: 'modern', paper: 'white', cover: 'photo', photos: 'color' },
  },
} as const satisfies Record<string, { name: string; blurb: string; look: CookbookLook }>
export type CookbookLookId = keyof typeof COOKBOOK_LOOKS
export const COOKBOOK_LOOK_IDS = Object.keys(COOKBOOK_LOOKS) as CookbookLookId[]
export const CLASSIC_COOKBOOK: CookbookLook = COOKBOOK_LOOKS.classic.look

export const sameCookbookLook = (a: CookbookLook, b: CookbookLook) =>
  (Object.keys(a) as (keyof CookbookLook)[]).every((k) => a[k] === b[k])

// A look read back, or nothing if it isn't a whole, known look.
export const parseCookbookLook = (value: unknown): CookbookLook | null =>
  readDesign(COOKBOOK, { from: 'classic', look: value })?.look ?? null

// A recipe given choices of its own: where its picture sits and its edges, by the recipe's id.
export type RecipeEdits = PageEdits<typeof COOKBOOK_RECIPE>
export type RecipeEdit = RecipeEdits[string]

export type ChosenCookbookLook = { from: CookbookLookId; look: CookbookLook; pages?: RecipeEdits }
export const readChosenCookbookLook = (value: unknown): ChosenCookbookLook | null =>
  readDesign(COOKBOOK, value, COOKBOOK_RECIPE)
